// useDashboardModalState.js

import { useCallback, useEffect, useMemo, useState } from 'react';

// Context
import { useUser } from 'context/UserContext';
import { useBase } from 'context/BaseContext';

// Routing
import { useNavigate } from 'react-router-dom';

// Services
import { createDashboard, updateDashboard } from 'services/dashboards/DashboardWriteService';

/**
 * useDashboardModalState
 *
 * Manages draft state, reset behavior, and submit flows for DashboardModal.
 *
 * @param {object} params - Hook parameters.
 * @param {boolean} params.isOpen - Whether the modal is open.
 * @param {function} params.onClose - Called when the modal is dismissed.
 * @param {function} [params.onCreated] - Called after a dashboard is created successfully.
 * @param {object|null} [params.dashboard=null] - Existing dashboard when editing.
 *
 * @returns {object} Grouped modal state and handlers for DashboardModal.
 */
export default function useDashboardModalState({
    isOpen,
    onClose,
    onCreated,
    dashboard = null,
}) {
    const { user } = useUser();
    const { activeBase } = useBase();
    const navigate = useNavigate();

    const [title, setTitle] = useState('');
    const [description, setDescription] = useState('');
    const [isPrivate, setIsPrivate] = useState(false);
    const [isSubmitting, setIsSubmitting] = useState(false);

    const editDashboardKey = String(dashboard?.key || '').trim();
    const isEditMode = Boolean(editDashboardKey);

    useEffect(() => {
        if (!isOpen) return;

        if (isEditMode) {
            setTitle(String(dashboard?.title || '').trim() || 'Untitled Dashboard');
            setDescription(String(dashboard?.description || '').trim());
            setIsPrivate(Boolean(dashboard?.private));
            setIsSubmitting(false);
            return;
        }

        setTitle('');
        setDescription('');
        setIsPrivate(false);
        setIsSubmitting(false);
    }, [dashboard?.description, dashboard?.private, dashboard?.title, isEditMode, isOpen]);

    const canSubmit = useMemo(() => {
        if (isSubmitting || !title.trim()) return false;
        if (isEditMode) return Boolean(editDashboardKey);
        return Boolean(activeBase?.key && user?.key);
    }, [activeBase?.key, editDashboardKey, isEditMode, isSubmitting, title, user?.key]);

    /**
     * handleClose
     *
     * Closes the modal unless a submit is currently in progress.
     */
    const handleClose = useCallback(() => {
        if (isSubmitting) return;
        onClose?.();
    }, [isSubmitting, onClose]);

    /**
     * handleSubmit
     *
     * Creates a new dashboard or updates the current one based on modal mode.
     *
     * @returns {Promise<void>} Resolves when the submit flow completes.
     */
    const handleSubmit = useCallback(async () => {
        if (!canSubmit) return;

        setIsSubmitting(true);

        try {
            if (isEditMode) {
                const normalizedTitle = String(title || '').trim();
                const nextTitle = normalizedTitle || 'Untitled Dashboard';
                const nextDescription = String(description || '').trim();
                const currentTitle = String(dashboard?.title || '').trim() || 'Untitled Dashboard';
                const currentDescription = String(dashboard?.description || '').trim();
                const currentPrivate = Boolean(dashboard?.private);

                if (nextTitle !== currentTitle || nextDescription !== currentDescription || isPrivate !== currentPrivate) {
                    await updateDashboard(editDashboardKey, {
                        title: nextTitle,
                        titleLower: nextTitle.toLowerCase(),
                        description: nextDescription,
                        private: isPrivate,
                    });
                }

                handleClose();
                return;
            }

            const created = await createDashboard({
                baseKey: activeBase.key,
                userKey: user.key,
                title,
                description,
                isPrivate,
                source: 'BASE',
            });

            const dashboardKey = created?.key;
            if (dashboardKey) {
                handleClose();
                onCreated?.();
                navigate(`/dashboard/${dashboardKey}`);
                setTitle('');
                setDescription('');
                setIsPrivate(false);
            }
        } catch (error) {
            console.error('[DashboardModal] submit dashboard error:', error);
        } finally {
            setIsSubmitting(false);
        }
    }, [activeBase?.key, canSubmit, dashboard?.description, dashboard?.private, dashboard?.title, description, editDashboardKey, handleClose, isEditMode, isPrivate, navigate, onCreated, title, user?.key]);

    return {
        modalState: {
            isEditMode,
            isSubmitting,
            canSubmit,
        },
        formState: {
            title,
            setTitle,
            description,
            setDescription,
            isPrivate,
            setIsPrivate,
        },
        handlers: {
            handleClose,
            handleSubmit,
        },
    };
}